/** @format */

import { useState, useEffect, useMemo } from 'react';
import { usePrevious } from './usePrevious';

export type UseCarouselDotsProps = {
  total: number;
  current: number;
  size?: number;
  margin?: number;
  padding?: number;
  visible?: number;
};

const range = (start: number, end: number): number[] => {
  if (end < start) return [];

  return [...Array(end - start + 1).keys()].map(idx => start + idx);
};

const defaultHolderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  transition: 'all 0.5s ease',
};

const defaultDotsStyle: React.CSSProperties = {
  flexShrink: 0,
  transition: 'all 0.5s ease',
};

const defaultDotStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  flexShrink: 0,
  transition: 'transform 0.5s ease, background 0.3s ease-in-out, opacity 0.3s ease-in-out',
};

const defaultActiveDotStyle: React.CSSProperties = {
  backgroundColor: 'white',
};

const defaultVisibleDotStyle: React.CSSProperties = {
  opacity: 1,
};

export const useCarouselDots = ({
  total,
  current,
  size = 6,
  margin = 3,
  padding = 16,
  visible = 3,
}: UseCarouselDotsProps) => {
  const _visible = total <= 3 ? total : visible;

  const prevCurrent = usePrevious(current);
  const dotFullWidth = size + margin * 2;
  const center = Math.round(_visible / 2);
  const centerIndex = Math.floor(_visible / 2);

  const [translate, setTranslate] = useState<number>(0);
  const [visibleDots, setVisibleDots] = useState<number[]>(range(0, _visible - 1));
  const [isLooping, setIsLooping] = useState<boolean>(false);

  useEffect(() => {
    if (current < centerIndex) {
      setTranslate(0);
      setVisibleDots(range(0, _visible - 1));
    } else if (total - center < current) {
      setTranslate((total - _visible) * dotFullWidth);
      setVisibleDots(range(total - _visible, total - 1));
    } else {
      setTranslate((current - centerIndex) * dotFullWidth);
      setVisibleDots(range(current - centerIndex, current + centerIndex));
    }
  }, [current, center, centerIndex, dotFullWidth, total, _visible]);

  useEffect(() => {
    let loopingTimer: ReturnType<typeof setTimeout>;

    if ((prevCurrent === 0 && current === total - 1) || (prevCurrent === total - 1 && current === 0)) {
      setIsLooping(true);
      loopingTimer = setTimeout(() => setIsLooping(false), 300);
    }

    return () => {
      if (loopingTimer) clearTimeout(loopingTimer);
    };
  }, [current, prevCurrent, total]);

  const getHolderStyle: React.CSSProperties = {
    height: size,
    paddingLeft: padding,
    paddingRight: padding,
    width: _visible * dotFullWidth + padding * 2,
  };

  const getDotsStyle: React.CSSProperties = {
    height: size,
    width: size,
    marginRight: margin,
    marginLeft: margin,
    transform: `translateX(-${translate}px)`,
  };

  const getDotStyle: React.CSSProperties = {
    opacity: 0,
    ...(isLooping && { opacity: 1, transform: 'scale(1)' }),
  };

  const dots = useMemo(() => range(0, total - 1), [total]);

  return {
    defaultHolderStyle,
    getHolderStyle,
    dots,
    defaultDotsStyle,
    getDotsStyle,
    defaultDotStyle,
    getDotStyle,
    defaultVisibleDotStyle,
    defaultActiveDotStyle,
    isActive: (dot: number) => current === dot,
    isVisible: (dot: number) => visibleDots.includes(dot),
  };
};
